import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Typography,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocalFireDepartmentRounded,
  EmojiEventsRounded,
  TimerRounded,
  CheckCircleRounded,
  ExitToAppRounded,
  CardGiftcardRounded,
  CelebrationRounded,
  PersonRounded,
  WorkspacePremiumRounded,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import { AuthContext } from '../../context/AuthContext';
import PageShell from '../../components/shared/PageShell';
import SurfaceCard from '../../components/shared/SurfaceCard';
import AnimatedNumber from '../../components/shared/AnimatedNumber';
import StatCard from '../../components/shared/StatCard';
import PillTabs from '../../components/shared/PillTabs';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { RADIUS, SHADOWS_LIGHT, SHADOWS_DARK } from '../../theme/tokens';

const COIN_TABS = [
  { key: 'history', label: 'History' },
  { key: 'earn', label: 'How to Earn' },
  { key: 'leaderboard', label: 'Leaderboard' },
];

// Small inline coin SVG — gradient stops from theme; no hardcoded hex
function CoinSvg({ size = 20 }) {
  const theme = useTheme();
  const accent = theme.palette.accent?.main ?? theme.palette.warning.main;
  const mid = theme.palette.warning.main;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="url(#coinSvgGrad)" stroke="none" />
      <text x="12" y="16" textAnchor="middle" fill="rgba(0,0,0,0.55)" fontSize="10" fontWeight="bold">B</text>
      <defs>
        <linearGradient id="coinSvgGrad" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
          <stop stopColor={accent} />
          <stop offset="0.5" stopColor={mid} />
          <stop offset="1" stopColor={accent} />
        </linearGradient>
      </defs>
    </svg>
  );
}

function getEventIcon(eventType, color) {
  const sx = { fontSize: 20, color };
  const map = {
    profile_completion: <PersonRounded sx={sx} />,
    checkin: <CheckCircleRounded sx={sx} />,
    checkout: <ExitToAppRounded sx={sx} />,
    pomodoro: <TimerRounded sx={sx} />,
    streak_7_day: <LocalFireDepartmentRounded sx={sx} />,
    streak_30_day: <EmojiEventsRounded sx={sx} />,
    referral: <CardGiftcardRounded sx={sx} />,
    bonus: <CelebrationRounded sx={sx} />,
  };
  return map[eventType] || <CoinSvg size={20} />;
}

function getEventLabel(eventType) {
  const labels = {
    profile_completion: 'Profile Completion',
    checkin: 'Check-in',
    checkout: 'Check-out',
    pomodoro: 'Pomodoro',
    streak_7_day: '7-Day Streak',
    streak_30_day: '30-Day Streak',
    referral: 'Referral',
    bonus: 'Bonus',
    redeemed: 'Redeemed',
    admin_adjustment: 'Adjustment',
  };
  return labels[eventType] || eventType;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// History card row
function TxCard({ tx, index }) {
  const theme = useTheme();
  const isCredit = tx.type === 'credit';
  const iconColor = isCredit ? theme.palette.success.main : theme.palette.error.main;
  const deltaColor = isCredit ? 'success.main' : 'error.main';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 8) * 0.045, type: 'spring', stiffness: 180, damping: 22 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.75,
          bgcolor: 'background.paper',
          borderRadius: RADIUS.lg,
          mb: 1,
          boxShadow: theme.palette.mode === 'light' ? SHADOWS_LIGHT.xs : SHADOWS_DARK.xs,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: isCredit
              ? `rgba(${theme.palette.mode === 'light' ? '22,163,74' : '74,222,128'},0.12)`
              : `rgba(${theme.palette.mode === 'light' ? '220,38,38' : '248,113,113'},0.12)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {getEventIcon(tx.event_type, iconColor)}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {getEventLabel(tx.event_type)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {formatDate(tx.created_at)}
          </Typography>
        </Box>

        <Typography variant="body2" fontWeight={700} color={deltaColor} sx={{ flexShrink: 0 }}>
          {isCredit ? '+' : '-'}{tx.amount}
        </Typography>
      </Box>
    </motion.div>
  );
}

// Leaderboard row
function LeaderboardRow({ entry, rank, isCurrentUser, theme }) {
  const isFirst = rank === 1;
  const glowShadow = theme.palette.mode === 'light' ? SHADOWS_LIGHT.glowAccent : SHADOWS_DARK.glowAccent;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: RADIUS.lg,
        mb: 0.75,
        bgcolor: isCurrentUser ? theme.palette.primary.soft ?? 'rgba(242,101,34,0.08)' : 'background.paper',
        boxShadow: isFirst ? glowShadow : undefined,
        border: isFirst ? `1px solid ${theme.palette.accent?.main ?? theme.palette.warning.main}` : '1px solid transparent',
      }}
    >
      <Box sx={{ width: 28, textAlign: 'center', flexShrink: 0 }}>
        {isFirst ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <WorkspacePremiumRounded sx={{ fontSize: 18, color: theme.palette.accent?.main ?? theme.palette.warning.main }} />
            <Typography variant="caption" fontWeight={800} color="warning.main">#1</Typography>
          </Box>
        ) : (
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            #{rank}
          </Typography>
        )}
      </Box>

      <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.875rem', fontWeight: 700 }}>
        {(entry.name || 'A')[0].toUpperCase()}
      </Avatar>

      <Typography variant="body2" fontWeight={isCurrentUser ? 700 : 600} sx={{ flex: 1, minWidth: 0 }} noWrap>
        {entry.name || 'Anonymous'}{isCurrentUser ? ' (You)' : ''}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <CoinSvg size={14} />
        <Typography variant="body2" fontWeight={700}>
          {entry.lifetime_earned?.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
}

function CoinsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [coinData, setCoinData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [rewardRates, setRewardRates] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardError, setLeaderboardError] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  const [page, setPage] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchCoinData();
    fetchRewardRates();
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [page]);

  const fetchCoinData = async () => {
    try {
      const response = await api.get('/coins/balance');
      setCoinData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch coin balance:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams({ page: page + 1, limit: PAGE_SIZE });
      const response = await api.get(`/coins/transactions?${params}`);
      setTransactions(response.data.data.transactions || []);
      setTotalTransactions(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  const fetchRewardRates = async () => {
    try {
      const response = await api.get('/coins/rewards');
      setRewardRates(response.data.data);
    } catch (err) {
      console.error('Failed to fetch reward rates:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await api.get('/coins/leaderboard?limit=10');
      const board = response.data.data.leaderboard || [];
      if (board.length === 0) {
        setLeaderboardError(true);
      }
      setLeaderboard(board);
    } catch (err) {
      setLeaderboardError(true);
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const visibleTabs = leaderboardError
    ? COIN_TABS.filter((t) => t.key !== 'leaderboard')
    : COIN_TABS;

  const currentUserInTop10 = leaderboard.some((e) => e.user_id === user?.id);
  const userEntry = leaderboard.find((e) => e.user_id === user?.id);

  const todayEarned = coinData?.today_earned ?? 0;
  const weekEarned = coinData?.week_earned ?? 0;
  const lifetimeEarned = coinData?.lifetime_earned ?? 0;

  const isNarrow = useMediaQuery('(max-width:320px)');

  if (loading) {
    return (
      <PageShell title="BML coins" backTo={-1}>
        <LoadingSkeleton variant="hero" sx={{ height: 180, mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ flex: 1 }}>
              <LoadingSkeleton variant="stat" />
            </Box>
          ))}
        </Box>
        <LoadingSkeleton variant="list" count={5} />
      </PageShell>
    );
  }

  return (
    <PageShell title="BML coins" backTo={-1}>
          {/* Coin hero */}
          <SurfaceCard
            sx={{ mb: 2.5, borderLeft: (t) => `4px solid ${t.palette.accent?.main ?? t.palette.warning.main}` }}
          >
            {(coinData?.current_streak ?? 0) > 0 && (
              <Chip
                icon={<LocalFireDepartmentRounded sx={{ fontSize: 16 }} />}
                label={`${coinData.current_streak} day streak`}
                size="small"
                sx={{ mb: 1, bgcolor: 'primary.soft', color: 'primary.main', fontWeight: 600 }}
              />
            )}
            <Typography variant="overline" color="text.secondary" display="block">BML coins</Typography>
            <Typography
              variant="display"
              fontWeight={800}
              color="text.primary"
              aria-label={`${coinData?.balance ?? 0} BML coins`}
            >
              <AnimatedNumber
                value={coinData?.balance ?? 0}
                format={(n) => Math.round(n).toLocaleString('en-IN')}
                duration={0.9}
              />
            </Typography>
          </SurfaceCard>

          {/* Stat tiles — 3-col grid on >=360px, horizontal scroll on <=320px */}
          {isNarrow ? (
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                mb: 3,
                overflowX: 'auto',
                pb: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {[
                { label: 'Today', value: todayEarned, icon: <CoinSvg size={24} /> },
                { label: 'This week', value: weekEarned, icon: <CoinSvg size={24} /> },
                { label: 'Lifetime', value: lifetimeEarned, icon: <EmojiEventsRounded /> },
              ].map(({ label, value, icon }) => (
                <Box key={label} sx={{ minWidth: 110, flex: '0 0 auto' }}>
                  <StatCard icon={icon} label={label} value={value} tone="coin" />
                </Box>
              ))}
            </Box>
          ) : (
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {[
                { label: 'Today', value: todayEarned, icon: <CoinSvg size={24} /> },
                { label: 'This week', value: weekEarned, icon: <CoinSvg size={24} /> },
                { label: 'Lifetime', value: lifetimeEarned, icon: <EmojiEventsRounded /> },
              ].map(({ label, value, icon }) => (
                <Grid item xs={4} key={label}>
                  <StatCard icon={icon} label={label} value={value} tone="coin" />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Tabs */}
          <Box sx={{ mb: 2.5 }}>
            <PillTabs
              tabs={visibleTabs}
              value={activeTab}
              onChange={setActiveTab}
              fullWidth
            />
          </Box>

          {/* Tab panels */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
            >
              {activeTab === 'history' && (
                <>
                  {transactions.length === 0 ? (
                    <EmptyState
                      icon={<TimerRounded />}
                      title="No coins earned yet"
                      description="Start a Pomodoro session to earn your first coins!"
                      actionLabel="Go to Focus Mode"
                      onAction={() => navigate('/student/focus-mode')}
                    />
                  ) : (
                    <>
                      {transactions.map((tx, idx) => (
                        <TxCard key={tx.id} tx={tx} index={idx} />
                      ))}
                      {transactions.length < totalTransactions && (
                        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setPage((p) => p + 1)}
                            sx={{ fontWeight: 600, color: 'text.secondary' }}
                          >
                            Load more
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

              {activeTab === 'earn' && rewardRates && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(rewardRates.rewards).map(([key, value]) => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: RADIUS.lg,
                        boxShadow: theme.palette.mode === 'light' ? SHADOWS_LIGHT.xs : SHADOWS_DARK.xs,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: 'rgba(247,179,43,0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.accent?.main ?? theme.palette.warning.main,
                          flexShrink: 0,
                        }}
                      >
                        {getEventIcon(key, theme.palette.accent?.main ?? theme.palette.warning.main)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {getEventLabel(key)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rewardRates.descriptions?.[key] ?? ''}
                        </Typography>
                      </Box>
                      <Chip
                        label={`+${value}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(247,179,43,0.12)',
                          color: theme.palette.accent?.main ?? theme.palette.warning.main,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {activeTab === 'leaderboard' && (
                <>
                  {leaderboard.length === 0 ? (
                    <EmptyState
                      icon={<EmojiEventsRounded />}
                      title="Leaderboard coming soon"
                      description="Be the first to earn coins and top the charts!"
                    />
                  ) : (
                    <>
                      {leaderboard.map((entry) => (
                        <LeaderboardRow
                          key={entry.user_id}
                          entry={entry}
                          rank={entry.rank}
                          isCurrentUser={entry.user_id === user?.id}
                          theme={theme}
                        />
                      ))}
                      {/* Sticky footer if current user not in top 10 */}
                      {!currentUserInTop10 && (
                        <Box
                          sx={{
                            position: 'sticky',
                            bottom: 80,
                            mt: 1,
                            p: 1.5,
                            bgcolor: theme.palette.background.elevated,
                            borderRadius: RADIUS.lg,
                            border: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.875rem', fontWeight: 700 }}>
                            {(user?.name || 'Y')[0].toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                            You
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            #{userEntry ? userEntry.rank : '--'}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </motion.div>
          </AnimatePresence>
    </PageShell>
  );
}

export default CoinsPage;
